import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import Button from 'sushiweb/Button';
import Layout from 'sushiweb/Layout';
import MenuItem from 'sushiweb/MenuItem';
import Checkbox from 'sushiweb/InputTypes/Checkbox';
import Dropdown from 'sushiweb/Dropdown/Dropdown';

import { genericErrorMsg, questionStatusEnum } from '../../../../common/components/constants';
import SelectItems from '../../../../../components/SelectItems';
import {
  InputWrapper,
  StyledInput,
  StyledLayout,
  NeverEnd,
  PolicyButton,
  ErrorContainerV2,
  SlotHeaderContainer,
  PolicyFormBody,
  PolicyFormFooter,
  SlotRuleTitle,
  AmountInput,
  FrequencyBudgetRow,
  FrequencyBudgetField,
  BudgetInputField,
  FrequencyLabel,
  FrequencyDropdownWrapper,
  SlotBudgetInputWrapper,
} from '../../../styles';
import { frequencyTypes } from '../../../../WalletPrograms/constants';
import {
  handleOptionNameChange,
  handleAmountChange,
  BottomScroll,
  transformFormDataToApiFormat,
} from '../../../common/helpers/inputHandlers';
import {
  getBusinessTypesData,
  getDaysData,
  getQuestionsData,
  getLocationsData,
  getSlotDetails,
  validateSlotData,
  updatePolicyUnSelectedDays,
  updateSelectedBusinessTypes,
  updateUnSelectedBusinessTypes,
  updatePolicySelectedDays,
  updateSelectedQuestion,
  updateUnSelectedQuestion,
  updateSlotStartTime,
  updateSlotEndTime,
  updateSlotSelectedDays,
  updateSlotUnSelectedDays,
  updateSlotBudget,
  updateSlotPerOrderBudget,
  updateSlotSelectedLocations,
  updateSlotUnSelectedLocations,
  updateSlotLocationPermission,
  handleDeleteSlots,
  handleCycleTypeSelect,
  addNewSlot,
} from './util';

import {
  CircledPlusFilledIcon,
  TrashIcon,
} from '../../../../../components/SVGs/commonSVGs';
import { GET_ALL_POI } from '../../../../../constants/urls';
import zfetch, { METHOD_GET } from '../../../../common/utils/zfetch';

const CreatePolicyForm = ({
  companyId,
  selectedWorkspace,
  showErrorToast,
  fetchingProgramInfo,
  fetchedProgramInfo,
  programInfo,
  questions,
  getAllQuestions,
  addOptions,
  updateOption,
  optionData,
  onClose,
}) => {
  const [errorValidation, setErrorValidation] = useState([]);
  const [locationsData, setLocationsData] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const { questionsData = [] } = questions;

  useEffect(() => {
    getAllQuestions({ companyId });
  }, [companyId]);

  const {
    allBusinessTypes,
    selectedBusinessTypes,
    unSelectedBusinessTypes,
  } = getBusinessTypesData(optionData);

  const { allDays, selectedDays, unSelectedDays } = getDaysData(optionData);

  useEffect(() => {
    const options = {
      method: METHOD_GET,
    };
    zfetch(`${GET_ALL_POI}?company_id=${companyId}`, options)
      .then(res => res.json())
      .then(res => {
        if (!res?.status) {
          showErrorToast('Unable to get location details');
          return;
        }
        const { data: offices = [] } = res;
        setLocationsData(offices);
      })
      .catch(() => {
        showErrorToast(genericErrorMsg);
      });
  }, [companyId]);

  const {
    allQuestions,
    selectedQuestions,
    unSelectedQuestions,
  } = getQuestionsData(questionsData, optionData, questionStatusEnum);

  const { allLocations } = getLocationsData(locationsData);

  const [formData, setFormData] = useState({
    optionName: optionData?.title ?? '',
    optionId: optionData?.id ?? '',
    selectedBusinessTypes: [...selectedBusinessTypes],
    unSelectedBusinessTypes: [...unSelectedBusinessTypes],
    selectedDays: [...selectedDays],
    unSelectedDays: [...unSelectedDays],
    selectedQuestions: [...selectedQuestions],
    unSelectedQuestions: [...unSelectedQuestions],
    slots: getSlotDetails(
      optionData?.restriction_details?.day_res ?? [],
      allLocations,
      selectedDays,
    ),
    nameErrMsg: '',
    workspaceID: selectedWorkspace?.workspace_id,
    cycleType:
      optionData?.restriction_details?.cycle_type ?? frequencyTypes.Monthly,
    amount: optionData?.restriction_details?.amount ?? '',
    restrictionsType: 2,
    isAdvancedRulesEnabled:
      getSlotDetails(
        optionData?.restriction_details?.day_res ?? [],
        allLocations,
        selectedDays,
      ).length > 0,
  });

  useEffect(() => {
    if (!formData?.isAdvancedRulesEnabled) {
      updateFormData({ slots: [] });
    }
  }, [formData?.isAdvancedRulesEnabled]);

  useEffect(() => {
    if (locationsData.length === 0) return;
    if (optionData?.restriction_details?.day_res?.length) {
      updateFormData({
        slots: getSlotDetails(
          optionData?.restriction_details?.day_res ?? [],
          allLocations,
          selectedDays,
        ),
      });
    } else {
      setFormData(prev => ({
        ...prev,
        slots: prev.slots.map(slot => ({
          ...slot,
          unSelectedLocations: allLocations.filter(
            loc =>
              !slot.selectedLocations.some(
                sel =>
                  parseInt(sel.value, 10) === parseInt(loc.value, 10),
              ),
          ),
        })),
      }));
    }
  }, [locationsData]);

  const updateFormData = newData => {
    setFormData(oldData => ({ ...oldData, ...newData }));
  };

  useEffect(() => {
    if (
      programInfo?.program_id &&
      !fetchingProgramInfo &&
      fetchedProgramInfo
    ) {
      updateFormData({
        optionName: optionData?.title ?? '',
        optionId: optionData?.id ?? '',
        selectedBusinessTypes: [...selectedBusinessTypes],
        unSelectedBusinessTypes: [...unSelectedBusinessTypes],
        selectedDays: [...selectedDays],
        unSelectedDays: [...unSelectedDays],
        selectedQuestions: [...selectedQuestions],
        unSelectedQuestions: [...unSelectedQuestions],
        slots: getSlotDetails(
          optionData?.restriction_details?.day_res ?? [],
          allLocations,
          selectedDays,
        ),
        nameErrMsg: '',
        workspaceID: selectedWorkspace?.workspace_id,
        cycleType:
          optionData?.restriction_details?.cycle_type ?? frequencyTypes.Monthly,
        amount: optionData?.restriction_details?.amount ?? '',
        restrictionsType: 2,
        isAdvancedRulesEnabled:
          getSlotDetails(
            optionData?.restriction_details?.day_res ?? [],
            allLocations,
            selectedDays,
          ).length > 0,
      });
    }
  }, [fetchingProgramInfo]);

  useEffect(() => {
    const shouldEnable =
      !!formData?.optionName &&
      !!formData?.selectedBusinessTypes?.length &&
      !!formData?.selectedDays?.length &&
      !!formData?.amount;

    if (shouldEnable && disabled) setDisabled(false);
    else if (!shouldEnable && !disabled) setDisabled(true);
  }, [formData, formData?.isAdvancedRulesEnabled, formData?.slots, formData?.amount]);

  const handleNextBtnClick = () => {
    setErrorValidation([]);
    const { isValid, errorMsg } = validateSlotData(
      formData,
      programInfo?.start_time ?? '00:00',
    );
    if (!isValid) {
      setErrorValidation([errorMsg]);
      updateFormData({ showError: true });
      return;
    }

    const questionId =
      programInfo?.questions_details?.length > 0
        ? programInfo?.questions_details[0]?.question_id
        : '';

    const transformedData = transformFormDataToApiFormat(
      formData,
      companyId,
      questionId,
      optionData?.id,
    );
    if (optionData?.id) {
      updateOption(transformedData);
    } else {
      addOptions(transformedData);
    }

    if (onClose) onClose();
  };

  const renderSlotHeader = index => (
    <SlotHeaderContainer>
      <Layout.Item colD={6} colT={12} colM={12}>
        <SlotRuleTitle>{`Slot level rule #${index + 1}`}</SlotRuleTitle>
      </Layout.Item>
      <Layout.Item colD={6} colT={12} colM={12} style={{ textAlign: 'right' }}>
        <PolicyButton
          onClick={handleDeleteSlots(updateFormData, formData)(index)}
          size='small'
          appearance='link'
          icon={<TrashIcon />}
        >
          Delete slot
        </PolicyButton>
      </Layout.Item>
    </SlotHeaderContainer>
  );

  const renderSlots = () =>
    formData?.slots?.map((slot, index) => (
      <React.Fragment key={slot.id}>
        {renderSlotHeader(index)}
        <StyledLayout>
          <Layout.Item colD={6} colT={12} colM={12}>
            <InputWrapper>
              <StyledInput
                label='Start Time'
                type='time'
                value={slot?.startTime ?? '00:00'}
                innerWidth='100%'
                onChange={updateSlotStartTime(updateFormData, formData)(index)}
                showHelperText
                style={{ paddingRight: '15px' }}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={12} colM={12}>
            <InputWrapper>
              <StyledInput
                label='End Time'
                type='time'
                value={slot?.endTime ?? '23:59'}
                innerWidth='100%'
                onChange={updateSlotEndTime(updateFormData, formData)(index)}
                showHelperText
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <SelectItems
                allItems={formData?.selectedDays}
                selectedItems={slot?.selectedDays || []}
                unSelectedItems={slot?.unSelectedDays || []}
                updateSelectedItems={
                  newDays =>
                    updateSlotSelectedDays(updateFormData, formData)(index, newDays)
                }
                updateUnSelectedItems={
                  newUnSelectedDays =>
                    updateSlotUnSelectedDays(updateFormData, formData)(
                      index,
                      newUnSelectedDays,
                    )
                }
                placeholder='Applicable days for this slot'
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={12} colM={12}>
            <SlotBudgetInputWrapper>
              <StyledInput
                value={slot?.slotBudget === 0 ? '' : slot?.slotBudget}
                innerWidth='100%'
                label='Slot budget'
                onChange={updateSlotBudget(updateFormData, formData)(index)}
                style={{ paddingRight: '15px' }}
              />
            </SlotBudgetInputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={12} colM={12}>
            <SlotBudgetInputWrapper>
              <StyledInput
                value={slot?.perOrderBudget === 0 ? '' : slot?.perOrderBudget}
                innerWidth='100%'
                label='Per order budget'
                onChange={updateSlotPerOrderBudget(updateFormData, formData)(index)}
              />
            </SlotBudgetInputWrapper>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <SelectItems
                allItems={allLocations}
                selectedItems={slot?.selectedLocations || []}
                unSelectedItems={slot?.unSelectedLocations || []}
                updateSelectedItems={
                  newLocations =>
                    updateSlotSelectedLocations(
                      updateFormData,
                      formData,
                      allLocations,
                    )(index, newLocations)
                }
                updateUnSelectedItems={
                  newUnSelectedLocations =>
                    updateSlotUnSelectedLocations(updateFormData, formData)(
                      index,
                      newUnSelectedLocations,
                    )
                }
                placeholder='Applicable location(s) (Optional)'
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={6} colT={3} colM={3}>
            <NeverEnd>
              <NeverEnd.SlotContainer>
                <Checkbox
                  checked={!slot?.areLocationsPermitted}
                  onChange={() => updateSlotLocationPermission(updateFormData, formData)(index)}
                  label='Mark locations as prohibited'
                  color='#9197A6'
                  disabled={slot?.selectedLocations?.length === 0}
                />
              </NeverEnd.SlotContainer>
            </NeverEnd>
          </Layout.Item>
        </StyledLayout>
      </React.Fragment>
    ));

  const handleAdvancedRuleCheckBoxClick = () => {
    if (!formData?.isAdvancedRulesEnabled) {
      addNewSlot(updateFormData, formData, allLocations)();
    }
    updateFormData({
      isAdvancedRulesEnabled: !formData?.isAdvancedRulesEnabled,
    });
  };

  const renderAddNewSlotButton = () => {
    if (formData?.slots?.length >= 0 && formData?.slots?.length < 3) {
      return (
        <Layout.Item colD={6} colT={12} colM={12} style={{ textAlign: 'left' }}>
          <PolicyButton
            onClick={addNewSlot(updateFormData, formData, allLocations)}
            size='small'
            appearance='link'
            icon={<CircledPlusFilledIcon />}
          >
            Add new slot
          </PolicyButton>
        </Layout.Item>
      );
    }
    return null;
  };

  const frequencyName = frequencyTypes[formData?.cycleType];
  const budgetLabel = frequencyName
    ? `${frequencyName} budget per employee`
    : 'Budget per employee';

  return (
    <>
      <PolicyFormBody>
        <StyledLayout gap={8}>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <StyledInput
                value={formData?.optionName}
                innerWidth='100%'
                label='Food policy name'
                onChange={handleOptionNameChange(updateFormData)}
                errorMsg={formData?.nameErrMsg}
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <SelectItems
                allItems={allBusinessTypes}
                selectedItems={formData?.selectedBusinessTypes}
                unSelectedItems={formData?.unSelectedBusinessTypes}
                updateSelectedItems={updateSelectedBusinessTypes(updateFormData)}
                updateUnSelectedItems={updateUnSelectedBusinessTypes(updateFormData)}
                placeholder='Applicable service(s)'
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <InputWrapper>
              <SelectItems
                allItems={allDays}
                selectedItems={formData?.selectedDays}
                unSelectedItems={formData?.unSelectedDays}
                updateSelectedItems={updatePolicySelectedDays(updateFormData)}
                updateUnSelectedItems={updatePolicyUnSelectedDays(updateFormData)}
                placeholder='Applicable days(s)'
              />
            </InputWrapper>
          </Layout.Item>
          <Layout.Item colD={12} colT={12} colM={12}>
            <FrequencyBudgetRow>
              <FrequencyBudgetField>
                <FrequencyLabel>Frequency</FrequencyLabel>
                <FrequencyDropdownWrapper>
                  <Dropdown
                    value={frequencyTypes[formData?.cycleType]}
                    width='100%'
                    placeholder='Top-up frequency'
                    onSelect={handleCycleTypeSelect(updateFormData)}
                    fontSize='1.6rem'
                    customPadding='1.3rem 1.4rem'
                  >
                    <MenuItem value={frequencyTypes.Daily} label={frequencyTypes[3]} />
                    <MenuItem value={frequencyTypes.Weekly} label={frequencyTypes[1]} />
                    <MenuItem value={frequencyTypes.Monthly} label={frequencyTypes[2]} />
                  </Dropdown>
                </FrequencyDropdownWrapper>
              </FrequencyBudgetField>
              <BudgetInputField>
                <AmountInput
                  value={formData?.amount}
                  innerWidth='100%'
                  label={budgetLabel}
                  onChange={handleAmountChange(updateFormData)}
                  errorMsg={formData?.amountErrMsg}
                />
              </BudgetInputField>
            </FrequencyBudgetRow>
          </Layout.Item>
          {questionsData?.length > 0 && (
            <Layout.Item colD={12} colT={12} colM={12}>
              <InputWrapper>
                <SelectItems
                  allItems={allQuestions}
                  selectedItems={formData?.selectedQuestions}
                  unSelectedItems={formData?.unSelectedQuestions}
                  updateSelectedItems={updateSelectedQuestion(updateFormData)}
                  updateUnSelectedItems={updateUnSelectedQuestion(updateFormData)}
                  placeholder='Questions to employees (Optional)'
                />
              </InputWrapper>
            </Layout.Item>
          )}
        </StyledLayout>
        {formData?.isAdvancedRulesEnabled && renderSlots()}
        {formData?.isAdvancedRulesEnabled && renderAddNewSlotButton()}
        {formData?.showError && errorValidation?.length > 0 && (
          <>
            <BottomScroll />
            <ErrorContainerV2>
              {errorValidation.map(err => (
                <p key={err}>{err}</p>
              ))}
            </ErrorContainerV2>
          </>
        )}
      </PolicyFormBody>
      <PolicyFormFooter>
        <Checkbox
          label='Add advanced rules'
          onChange={handleAdvancedRuleCheckBoxClick}
          checked={formData?.isAdvancedRulesEnabled}
          disabled={disabled}
          color='#EF4F5F'
        />
        <Button onClick={handleNextBtnClick} disabled={disabled}>
          Continue
        </Button>
      </PolicyFormFooter>
    </>
  );
};

CreatePolicyForm.propTypes = {
  companyId: PropTypes.string,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  showErrorToast: PropTypes.func,
  programInfo: PropTypes.objectOf(PropTypes.any),
  fetchingProgramInfo: PropTypes.bool,
  fetchedProgramInfo: PropTypes.bool,
  questions: PropTypes.objectOf(PropTypes.any),
  getAllQuestions: PropTypes.func,
  addOptions: PropTypes.func,
  updateOption: PropTypes.func,
  optionData: PropTypes.objectOf(PropTypes.any),
  onClose: PropTypes.func,
};

CreatePolicyForm.defaultProps = {
  companyId: '',
  selectedWorkspace: {},
  showErrorToast: _noop,
  programInfo: {},
  getAllQuestions: _noop,
  fetchedProgramInfo: true,
  fetchingProgramInfo: true,
  questions: {},
  addOptions: _noop,
  updateOption: _noop,
  optionData: {},
  onClose: _noop,
};

export default CreatePolicyForm;
